package top.wcpe.allincore.ui.runtime.ir.eval

import top.wcpe.allincore.layout.engine.Align
import top.wcpe.allincore.layout.engine.Dimension
import top.wcpe.allincore.layout.engine.FlexWrap
import top.wcpe.allincore.layout.engine.Justify
import kotlin.jvm.JvmStatic

/**
 * IR 字面量值到强类型的统一转换工具。
 *
 * 集中了原本散落在 PropAppliers 中的 7 个 `to*` 私有静态方法，
 * 规则只有一份：未识别值统一抛 [IllegalStateException]。
 */
@Suppress("TooManyFunctions")
object ValueCoercion {

    /** fill 等价于 100%。 */
    private const val FILL_PERCENT = 100f

    /** 十六进制基数。 */
    private const val HEX_RADIX = 16

    /** 默认降中心。 */
    private const val DEFAULT_ORIGIN = 0.5f

    /** 百分比转换除数。 */
    private const val PERCENT_DIVISOR = 100f

    /** Number → px Dimension；null → auto；字符串 → 解析为 auto / fill / 百分比 / 像素。 */
    @JvmStatic
    fun toDimension(v: Any?): Dimension {
        if (v == null) return Dimension.auto()
        if (v is Number) return Dimension.px(v.toFloat())
        if (v is String) return parseDimension(v)
        throw IllegalStateException("非法的尺寸值: $v")
    }

    private fun parseDimension(raw: String): Dimension {
        val s = raw.trim()
        val lower = s.lowercase()
        if (lower == "auto") return Dimension.auto()
        // fill 视作父尺寸 100%：对 definite 父即填满，无需感知父的具体像素（与导航宽等解耦）
        if (lower == "fill") return Dimension.percent(FILL_PERCENT)
        return try {
            if (s.endsWith("%")) {
                Dimension.percent(s.substring(0, s.length - 1).trim().toFloat())
            } else {
                val num = if (lower.endsWith("px")) s.substring(0, s.length - 2).trim() else s
                Dimension.px(num.toFloat())
            }
        } catch (e: NumberFormatException) {
            throw IllegalStateException("非法的尺寸值: $raw")
        }
    }

    /**
     * 解析 transformOrigin 值为归一化比例 `[originX, originY]`（0=左/上，0.5=中心，1=右/下）。
     * 降级为盒中心 `{0.5, 0.5}`（不抛异常以免炸 DSL）。
     */
    @JvmStatic
    fun toOrigin(v: Any?): FloatArray {
        if (v !is String) return floatArrayOf(DEFAULT_ORIGIN, DEFAULT_ORIGIN)
        val parts = v.trim().lowercase().split("\\s+".toRegex()).filter { it.isNotEmpty() }
        if (parts.isEmpty()) return floatArrayOf(DEFAULT_ORIGIN, DEFAULT_ORIGIN)
        if (parts.size == 1) return parseOriginSingle(parts[0])
        return parseOriginPair(parts[0], parts[1])
    }

    private fun parseOriginSingle(token: String): FloatArray {
        if (token == "top" || token == "bottom") {
            return floatArrayOf(DEFAULT_ORIGIN, parseOriginAxis(token, xAxis = false))
        }
        return floatArrayOf(parseOriginAxis(token, xAxis = true), DEFAULT_ORIGIN)
    }

    private fun parseOriginPair(a: String, b: String): FloatArray {
        var x = DEFAULT_ORIGIN
        var y = DEFAULT_ORIGIN
        var xSet = false
        var ySet = false
        // 先吃掉明确的轴关键字（顺序无关）
        val tokens = arrayOf(a, b)
        val consumed = BooleanArray(2)
        for (i in 0..1) {
            when (tokens[i]) {
                "left" -> {
                    x = 0f
                    xSet = true
                    consumed[i] = true
                }
                "right" -> {
                    x = 1f
                    xSet = true
                    consumed[i] = true
                }
                "top" -> {
                    y = 0f
                    ySet = true
                    consumed[i] = true
                }
                "bottom" -> {
                    y = 1f
                    ySet = true
                    consumed[i] = true
                }
            }
        }
        for (i in 0..1) {
            if (consumed[i]) continue
            val value = parseOriginAxis(tokens[i], xAxis = true)
            if (!xSet) {
                x = value
                xSet = true
            } else if (!ySet) {
                y = value
                ySet = true
            }
        }
        return floatArrayOf(x, y)
    }

    private fun parseOriginAxis(token: String, xAxis: Boolean): Float = when (token) {
        "center" -> DEFAULT_ORIGIN
        "left" -> if (xAxis) 0f else DEFAULT_ORIGIN
        "right" -> if (xAxis) 1f else DEFAULT_ORIGIN
        "top" -> if (xAxis) DEFAULT_ORIGIN else 0f
        "bottom" -> if (xAxis) DEFAULT_ORIGIN else 1f
        else -> {
            try {
                if (token.endsWith("%")) {
                    token.substring(0, token.length - 1).trim().toFloat() / PERCENT_DIVISOR
                } else {
                    token.toFloat()
                }
            } catch (e: NumberFormatException) {
                DEFAULT_ORIGIN
            }
        }
    }

    /** Number → float；其它类型抛错。 */
    @JvmStatic
    fun toFloat(v: Any?): Float {
        if (v is Number) return v.toFloat()
        throw IllegalStateException("非法的浮点值: $v")
    }

    /**
     * 解析宽高比（宽 / 高）：[Number] → toFloat；"A/B" 比值字符串 → A÷B。
     * 非法 / 无法识别 / 分母为 0 一律降级为 [Float.NaN]（对齐容错风格，不抛异常以免炸 DSL）。
     */
    @JvmStatic
    fun toAspectRatio(v: Any?): Float {
        if (v is Number) return v.toFloat()
        if (v is String) return parseAspectRatio(v)
        return Float.NaN
    }

    private fun parseAspectRatio(raw: String): Float {
        val s = raw.trim()
        return try {
            val slash = s.indexOf('/')
            if (slash < 0) {
                s.toFloat()
            } else {
                val a = s.substring(0, slash).trim().toFloat()
                val b = s.substring(slash + 1).trim().toFloat()
                if (b == 0f) Float.NaN else a / b
            }
        } catch (e: NumberFormatException) {
            Float.NaN
        }
    }

    /** Number → double；其它类型抛错。 */
    @JvmStatic
    fun toDouble(v: Any?): Double {
        if (v is Number) return v.toDouble()
        throw IllegalStateException("非法的双精度浮点值: $v")
    }

    /** Boolean / 数字（0=false） / 字符串（true/false/yes/no/1/0/空） → boolean。 */
    @Suppress("ComplexCondition")
    @JvmStatic
    fun toBool(v: Any?): Boolean {
        if (v is Boolean) return v
        if (v is Number) return v.toInt() != 0
        if (v is String) {
            val s = v.trim().lowercase()
            if (s == "true" || s == "1" || s == "yes") return true
            if (s == "false" || s == "0" || s == "no" || s.isEmpty()) return false
        }
        throw IllegalStateException("非法的布尔值: $v")
    }

    /**
     * Number → ARGB int；字符串支持 `0x` / `#` 前缀的 16 进制。
     * 用 Long 解析避免高位为 1 时 Integer.parseInt 溢出，再截断到 int。
     */
    @Suppress("MagicNumber")
    @JvmStatic
    fun toIntColor(v: Any?): Int {
        if (v is Number) return v.toLong().toInt()
        if (v is String) {
            val s = v.trim()
            val stripped = when {
                s.startsWith("0x") || s.startsWith("0X") -> s.substring(2)
                s.startsWith("#") -> s.substring(1)
                else -> throw IllegalStateException("非法的颜色值: $v")
            }
            return stripped.toLong(HEX_RADIX).toInt()
        }
        throw IllegalStateException("非法的颜色值: $v")
    }

    /** 字符串（大小写不敏感、`-` 视作 `_`） → [Align]。 */
    @JvmStatic
    fun toAlign(v: Any): Align {
        if (v !is String) throw IllegalStateException("align 需要字符串: $v")
        val n = v.trim().replace('-', '_').uppercase()
        return try {
            Align.valueOf(n)
        } catch (e: IllegalArgumentException) {
            throw IllegalStateException("非法 align: $v")
        }
    }

    /** 字符串（大小写不敏感、`-` 视作 `_`） → [Justify]。 */
    @Suppress("ThrowsCount")
    @JvmStatic
    fun toJustify(v: Any): Justify {
        if (v !is String) throw IllegalStateException("justify 需要字符串: $v")
        val n = v.trim().replace('-', '_').uppercase()
        if (n == "STRETCH") {
            throw IllegalStateException(
                "justify 不支持 \"stretch\"（Flex 主轴无 stretch 语义）；如需交叉轴拉伸请改用 align=\"stretch\"。",
            )
        }
        return try {
            Justify.valueOf(n)
        } catch (e: IllegalArgumentException) {
            throw IllegalStateException("非法 justify: $v")
        }
    }

    /** 字符串（大小写不敏感、`-` 视作 `_`） → [FlexWrap]。 */
    @JvmStatic
    fun toFlexWrap(v: Any): FlexWrap {
        if (v !is String) throw IllegalStateException("wrap 需要字符串: $v")
        val n = v.trim().replace('-', '_').uppercase()
        return try {
            FlexWrap.valueOf(n)
        } catch (e: IllegalArgumentException) {
            throw IllegalStateException("非法 wrap: $v（仅支持 nowrap / wrap / wrap-reverse）")
        }
    }
}
